import Axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";
import { toVault77ApiError } from "../../core/apiError";
import { EMPLOYEE_SENSITIVE_DATA_ENDPOINT } from "./constants";
import type {
    EmployeeSensitiveData,
    EmployeeSensitiveDataFilters,
    CreateEmployeeSensitiveDataRequest,
    UpdateEmployeeSensitiveDataRequest,
} from "../types";

/**
 * Fetch employee sensitive data records with optional filters.
 * GET api/vault77/employeeSensitiveData
 */
export async function getEmployeeSensitiveDataList(
    filters?: EmployeeSensitiveDataFilters
): Promise<EmployeeSensitiveData[]> {
    try {
        let url = EMPLOYEE_SENSITIVE_DATA_ENDPOINT;
        if (filters) {
            const params = new URLSearchParams();
            if (filters.employeeId != null) params.append("employeeId", String(filters.employeeId));
            if (filters.departmentId != null) params.append("departmentId", String(filters.departmentId));
            const query = params.toString();
            if (query) url = `${url}?${query}`;
        }
        const response = await Axios.get<EmployeeSensitiveData[]>(url, AxiosConfig.configValue("min"));
        return response.data;
    } catch (error) {
        console.error("getEmployeeSensitiveDataList/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch employee sensitive data.");
    }
}

/**
 * Fetch the single sensitive-data record for an employee (one row per employee), or null if none.
 * GET api/vault77/employeeSensitiveData?employeeId={employeeId}
 */
export async function getEmployeeSensitiveDataByEmployeeId(
    employeeId: number
): Promise<EmployeeSensitiveData | null> {
    const list = await getEmployeeSensitiveDataList({ employeeId });
    return list.length > 0 ? list[0] : null;
}

/**
 * Fetch a single sensitive-data record by its ID.
 * GET api/vault77/employeeSensitiveData/{id}
 */
export async function getEmployeeSensitiveDataById(id: number): Promise<EmployeeSensitiveData> {
    try {
        const response = await Axios.get<EmployeeSensitiveData>(
            `${EMPLOYEE_SENSITIVE_DATA_ENDPOINT}/${id}`,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("getEmployeeSensitiveDataById/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch sensitive data record.");
    }
}

/**
 * Create a sensitive-data record (one per employee; duplicate → 409).
 * POST api/vault77/employeeSensitiveData
 */
export async function createEmployeeSensitiveData(
    request: CreateEmployeeSensitiveDataRequest
): Promise<EmployeeSensitiveData> {
    try {
        const response = await Axios.post<EmployeeSensitiveData>(
            EMPLOYEE_SENSITIVE_DATA_ENDPOINT,
            request,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("createEmployeeSensitiveData/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to create sensitive data record.");
    }
}

/**
 * Update a sensitive-data record.
 * PUT api/vault77/employeeSensitiveData (ID is in the request body, not the URL)
 */
export async function updateEmployeeSensitiveData(
    request: UpdateEmployeeSensitiveDataRequest
): Promise<EmployeeSensitiveData> {
    try {
        const response = await Axios.put<EmployeeSensitiveData>(
            EMPLOYEE_SENSITIVE_DATA_ENDPOINT,
            request,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("updateEmployeeSensitiveData/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to update sensitive data record.");
    }
}

/**
 * Delete a sensitive-data record by ID (soft delete).
 * DELETE api/vault77/employeeSensitiveData/{id}
 */
export async function deleteEmployeeSensitiveData(id: number): Promise<void> {
    try {
        await Axios.delete(
            `${EMPLOYEE_SENSITIVE_DATA_ENDPOINT}/${id}`,
            AxiosConfig.configValue("min")
        );
    } catch (error) {
        console.error("deleteEmployeeSensitiveData/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to delete sensitive data record.");
    }
}
